// src/components/layout/SiteOnboarding.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SiteOnboarding.css';

function SiteOnboarding({ theme, setTheme, openAuthModal, manualStep, onManualClose }) {
    const [step, setStep] = useState(null); // null, 'theme', 'account'
    const [isVisible, setIsVisible] = useState(false);

    // Gérer le déclenchement manuel
    useEffect(() => {
        if (manualStep) {
            setStep(manualStep);
            setIsVisible(true);
        }
    }, [manualStep]);

    useEffect(() => {
        // Si déclenché manuellement, on ignore la vérification automatique du localStorage
        if (manualStep) return;

        // Vérifier si l'onboarding a déjà été complété
        const onboardingStatus = localStorage.getItem('infocolab_onboarding_status');

        if (!onboardingStatus) {
            // Premier démarrage : afficher le choix du thème
            // Petit délai pour ne pas agresser l'utilisateur dès le chargement du DOM
            const timer = setTimeout(() => {
                // Seulement si pas déjà affiché manuellement
                if (!isVisible) {
                    setStep('theme');
                    setIsVisible(true);
                }
            }, 1000);
            return () => clearTimeout(timer);
        } else if (onboardingStatus === 'theme_selected') {
            // Si l'utilisateur a choisi le thème mais pas encore vu/passé l'étape compte
            // (par ex s'il a rechargé la page entre temps)
            const timer = setTimeout(() => {
                if (!isVisible) {
                    setStep('account');
                    setIsVisible(true);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [manualStep, isVisible]);

    const handleThemeSelect = (selectedTheme) => {
        setTheme(selectedTheme);

        // Si c'est un déclenchement manuel (replay), on ferme juste après le choix
        if (manualStep) {
            if (onManualClose) onManualClose();
            setIsVisible(false);
            return;
        }

        // Sinon (flow standard onboarding), on sauvegarde l'état intermédiaire et on passe à la suite
        localStorage.setItem('infocolab_onboarding_status', 'theme_selected');

        // Transition vers l'étape suivante
        // On peut faire une petite animation ou attendre un peu
        setTimeout(() => {
            setStep('account');
        }, 300);
    };

    const navigate = useNavigate();

    const handleCreateAccount = () => {
        // Marquer comme complété avant d'ouvrir la modale
        if (!manualStep) {
            localStorage.setItem('infocolab_onboarding_status', 'completed');
        } else if (onManualClose) {
            onManualClose();
        }
        setIsVisible(false); // Fermer l'onboarding
        navigate('/inscription'); // Rediriger vers la page d'inscription
    };

    const handleLogin = () => {
        // Marquer comme complété avant d'ouvrir la modale
        if (!manualStep) {
            localStorage.setItem('infocolab_onboarding_status', 'completed');
        } else if (onManualClose) {
            onManualClose();
        }
        setIsVisible(false); // Fermer l'onboarding
        openAuthModal('login'); // Ouvrir la modale de connexion
    };

    const handleSkip = () => {
        if (manualStep && onManualClose) {
            onManualClose();
        } else {
            localStorage.setItem('infocolab_onboarding_status', 'completed');
        }
        setIsVisible(false);
    };

    if (!isVisible || !step) return null;

    return (
        <div className="onboarding-overlay">
            <div className="onboarding-modal">

                {/* ÉTAPE 1 : SÉLECTION DU THÈME */}
                {step === 'theme' && (
                    <>
                        <div className="onboarding-header">
                            <h2>Bienvenue chez INFO COLAB !</h2>
                            <p>Pour commencer, choisissez votre expérience visuelle préférée.</p>
                        </div>
                        <div className="onboarding-content">
                            <div className="theme-selection-grid">
                                <div
                                    className={`theme-option-card ${theme === 'light' ? 'selected' : ''}`}
                                    onClick={() => handleThemeSelect('light')}
                                >
                                    <span className="theme-icon">☀️</span>
                                    <span className="theme-label">Mode Clair</span>
                                </div>
                                <div
                                    className={`theme-option-card ${theme === 'dark' ? 'selected' : ''}`}
                                    onClick={() => handleThemeSelect('dark')}
                                >
                                    <span className="theme-icon">🌙</span>
                                    <span className="theme-label">Mode Sombre</span>
                                </div>
                            </div>
                            <p style={{ fontSize: '0.85rem', textAlign: 'center', opacity: 0.7, marginTop: '10px' }}>
                                Vous pourrez changer cela à tout moment dans le menu.
                            </p>
                        </div>
                    </>
                )}

                {/* ÉTAPE 2 : INCITATION AU COMPTE */}
                {step === 'account' && (
                    <>
                        <div className="onboarding-header">
                            <h2>Rejoignez la communauté</h2>
                            <p>Créez votre compte gratuitement pour profiter de tous nos services.</p>
                        </div>
                        <div className="onboarding-content">
                            <ul className="account-benefits">
                                <li className="account-benefit-item">
                                    <span className="benefit-icon">✓</span>
                                    <span>Suivez l'avancement de vos projets en temps réel</span>
                                </li>
                                <li className="account-benefit-item">
                                    <span className="benefit-icon">✓</span>
                                    <span>Accédez à vos factures et documents exclusifs</span>
                                </li>
                                <li className="account-benefit-item">
                                    <span className="benefit-icon">✓</span>
                                    <span>Demandez des devis et supports prioritaires</span>
                                </li>
                            </ul>

                            <div className="onboarding-actions">
                                <button className="btn-onboarding-primary pulse-animation" onClick={handleCreateAccount}>
                                    Créer mon compte maintenant <span>🚀</span>
                                </button>
                                <button className="btn-onboarding-secondary" onClick={handleSkip} style={{ marginTop: '5px' }}>
                                    Non merci, je visiterai en invité
                                </button>
                                <button className="btn-onboarding-secondary" onClick={handleLogin} style={{ textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', opacity: 0.8, marginTop: '2px' }}>
                                    J'ai déjà un compte
                                </button>
                            </div>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
}

export default SiteOnboarding;
